import React from 'react';
import { useDispatch } from 'react-redux';
import { Grid, IconButton, Tooltip } from '@mui/material';

import { Icon, useSettings, useUserData, Utils } from 'app/main/components';
import {
	DetailsSectionDescription,
	DetailsSectionImage,
	DetailsSectionInfoWrapper
} from 'app/main/components/page-layouts';
import { type DetailsPageContentProps } from 'app/main/components/page-layouts';
import DetailsSectionNotes from 'app/main/submodules/notes/DetailsSectionNotes';

import CompaniesDetailsSectionAddress from '../sections/CompaniesDetailsSectionAddress';
import CompaniesDetailsSectionBusinessCard from '../sections/CompaniesDetailsSectionBusinessCard';
import CompaniesDetailsSectionInfo from '../sections/CompaniesDetailsSectionInfo';
import CompaniesDetailsSectionNavInfo from '../sections/CompaniesDetailsSectionNavInfo';
import CompaniesDetailsSectionSecondaryInfo from '../sections/CompaniesDetailsSectionSecondaryInfo';
import CompaniesDetailsSectionSource from '../sections/CompaniesDetailsSectionSource';

export default function CompaniesDetailsTab(props: DetailsPageContentProps) {
	const dispatch = useDispatch();
	const { sysSettings, t } = useSettings();
	const userData = useUserData();

	const coordinatesConnector = Utils.getCoordinatesConnector({ sysSettings });

	const updateCoordinates = response => {
		Utils.executeStored({
			action: 'st_companies_update',
			data: {
				comp_uid: props.model.comp_uid,
				comp_user_modified_uid: userData.user_uid,
				comp_lat: response.data.lat,
				comp_lng: response.data.lng
			},
			dispatch,
			successHandler: () => {
				dispatch(Utils.alertSuccessMsg(t));
				props.updateRefresh?.();
			}
		});
	};

	const handleRefreshCoordinates = () => {
		Utils.getCoordinates({
			address: {
				address: props.model.comp_address,
				city: props.model.comp_city,
				zip: props.model.comp_zip,
				district: props.model.dist_title,
				country: props.model.ctry_title
			},
			dispatch,
			sysSettings
		})
			?.then(response => updateCoordinates(response))
			.catch(error => Utils.errorHandlerWrapper({ dispatch, error }));
	};

	return (
		<Grid container spacing={2}>
			<Grid size={{ lg: 8, xs: 12 }}>
				<Grid container spacing={2}>
					<Grid size={12}>
						<DetailsSectionInfoWrapper
							avatar={<Icon icon="text-subject" />}
							title={t('DETAILS_INFO_SECTION')}
							subheader={t('DETAILS_INFO_SUBHEADER')}
						>
							<CompaniesDetailsSectionInfo
								model={props.model}
								sendChange={props.sendChange}
								isEdit={props.isEdit}
								logProps={props.logProps}
							/>
						</DetailsSectionInfoWrapper>
					</Grid>
					{/* {(props.model.comp_erp_customer_id || props.model.comp_erp_supplier_id) && (
						<Grid size={12}>
							<DetailsSectionInfoWrapper
								avatar={<Icon icon="microsoft" />}
								title={t('DETAILS_NAV_INFO_SECTION')}
								subheader={t('DETAILS_NAV_INFO_SUBHEADER')}
							>
								<CompaniesDetailsSectionNavInfo
									model={props.model}
									sendChange={props.sendChange}
									isEdit={props.isEdit}
									logProps={props.logProps}
								/>
							</DetailsSectionInfoWrapper>
						</Grid>
					)} */}
					<Grid size={12}>
						<DetailsSectionInfoWrapper
							avatar={<Icon icon="text-subject" />}
							title={t('DETAILS_SECONDARY_INFO_SECTION')}
							subheader={t('DETAILS_SECONDARY_INFO_SUBHEADER')}
						>
							<CompaniesDetailsSectionSecondaryInfo
								model={props.model}
								sendChange={props.sendChange}
								isEdit={props.isEdit}
								logProps={props.logProps}
							/>
						</DetailsSectionInfoWrapper>
					</Grid>
					<Grid size={12}>
						<DetailsSectionDescription
							name="comp_descriptionhtml"
							value={props.model.comp_descriptionhtml}
							isEdit={props.isEdit}
							sendChange={props.sendChange}
							logProps={props.logProps}
						/>
					</Grid>
					<Grid size={12}>
						<DetailsSectionNotes module="companies" uid={props.model.comp_uid} />
					</Grid>
				</Grid>
			</Grid>
			<Grid size={{ lg: 4, xs: 12 }}>
				<Grid container spacing={2}>
					{(props.isAdd || props.isEdit) && (
						<Grid size={12}>
							<DetailsSectionImage
								title={t('COMPANIES_AVATAR_SECTION')}
								subheader={t('COMPANIES_AVATAR_SUBHEADER')}
								isEdit
								fieldName="comp_avatar"
								fileName={props.model.comp_uid}
								path={`companies\\${props.model.comp_uid}`}
								value={props.model.comp_avatar}
								// TODO: GOOGLE HA BLOCCATO LE CHIAMATE
								// grabberProps={{ defaultSearch: props.model.comp_title }}
								sendChange={props.sendFilesChange}
							/>
						</Grid>
					)}
					<Grid size={12}>
						<DetailsSectionInfoWrapper
							avatar={<Icon icon="map-marker" />}
							title={t('DETAILS_ADDRESS_SECTION')}
							subheader={t('DETAILS_ADDRESS_SUBHEADER')}
							headerAction={
								!props.isEdit &&
								!!coordinatesConnector && (
									<Tooltip title={t('DETAILS_COMPANIES_REFRESH_COORDINATES')} arrow>
										<IconButton onClick={handleRefreshCoordinates}>
											<Icon icon="crosshairs-gps" />
										</IconButton>
									</Tooltip>
								)
							}
						>
							<CompaniesDetailsSectionAddress
								model={props.model}
								sendChange={props.sendChange}
								isEdit={props.isEdit}
								logProps={props.logProps}
							/>
						</DetailsSectionInfoWrapper>
					</Grid>
					<Grid size={12}>
						<DetailsSectionInfoWrapper
							avatar={<Icon icon="clock-start" />}
							title={t('DETAILS_SOURCE_SECTION')}
							subheader={t('DETAILS_SOURCE_SUBHEADER')}
						>
							<CompaniesDetailsSectionSource
								model={props.model}
								sendChange={props.sendChange}
								isEdit={props.isEdit}
								logProps={props.logProps}
							/>
						</DetailsSectionInfoWrapper>
					</Grid>
					{(props.model.comp_business_card_front || props.model.comp_business_card_back || props.isEdit) && (
						<Grid size={12}>
							<CompaniesDetailsSectionBusinessCard
								uid={props.model.comp_uid}
								businessCard={{
									front: props.model.comp_business_card_front,
									back: props.model.comp_business_card_back
								}}
								isEdit={props.isEdit}
								sendFilesChange={props.sendFilesChange}
							/>
						</Grid>
					)}
					{/* {props.model.comp_statuses_dates?.length > 0 || props.isEdit ? (
						<Grid item xs={12}>
							<DetailsSectionStatusesDates
								module="companies"
								uid={props.model.comp_uid}
								systable="companies_relations"
								statusesDates={props.model.comp_statuses_dates}
								isEdit={props.isEdit}
								sendChange={props.sendChange}
							/>
						</Grid>
					) : (
						<></>
					)} */}
				</Grid>
			</Grid>
		</Grid>
	);
}
